import { AdPlatform } from '@marketing-tool/shared';
import type { Line, Statistics } from '@marketing-tool/shared';
import dayjs from 'dayjs';
import React from 'react';

import type { Ad, Media } from '../../data/game-report/types';
import * as remote from '../../data/remote-data';
import { trpc } from '../../lib/trpc';
import { useAllPages } from '../../lib/trpc-hooks';

export const useAdInterpolation = (
    gameId: string,
    platform: AdPlatform,
    adCampaignId: string | number,
    adGroupId?: string | number,
) => {
    const [result, setResult] = React.useState<remote.RemoteData<Record<string, string>, string>>(remote.NotAsked);

    const interpolate = React.useCallback(async () => {
        if (platform !== AdPlatform.Line) return;

        setResult(remote.Loading);
        try {
            const data = await trpc.line.ads.interpolate.mutate({
                gameId,
                adCampaignId: adCampaignId.toString(),
                adGroupId: adGroupId?.toString(),
            });
            setResult(remote.Success(data));
        } catch (error) {
            setResult(remote.Failure(error instanceof Error ? error.message : String(error)));
        }
    }, [gameId, platform, adCampaignId, adGroupId]);

    return React.useMemo(() => ({ data: result, interpolate }), [result, interpolate]);
};

type MediaWithStats = {
    id: number;
    type: 'IMAGE' | 'VIDEO' | 'APP' | 'URL' | 'ANIMATION';
    name: string;
    url?: string;
    createdDate?: string;
    ads: { id: string; name: string; cost: number; currency: string }[];
};

export const useMediaWithStats = (
    gameId: string,
): { data: remote.RemoteData<MediaWithStats[], string>; load: () => Promise<void> } => {
    const now = React.useMemo(() => dayjs(), []);
    const from = React.useMemo(() => now.subtract(30, 'day').format('YYYY-MM-DD'), [now]);
    const to = React.useMemo(() => now.format('YYYY-MM-DD'), [now]);

    const mediaQuery = useAllPages(
        (cursor, signal) => trpc.line.media.list.query({ gameId, cursor }, { signal }),
        [gameId],
    );

    const adsQuery = useAllPages(
        (cursor, signal) => trpc.line.ads.list.query({ gameId, cursor }, { signal }),
        [gameId],
    );

    const statsQuery = useAllPages(
        (cursor, signal) => trpc.line.statistics.list.query({ gameId, from, to, cursor }, { signal }),
        [gameId, from, to],
    );

    const data = React.useMemo(
        () =>
            remote.map3(mediaQuery.data, adsQuery.data, statsQuery.data, (allMedia, allAds, allStatistics) =>
                joinMediaWithStats(allMedia, allAds, allStatistics),
            ),
        [mediaQuery.data, adsQuery.data, statsQuery.data],
    );

    const load = React.useCallback(async () => {
        await Promise.all([mediaQuery.load(), adsQuery.load(), statsQuery.load()]);
    }, [mediaQuery, adsQuery, statsQuery]);

    return React.useMemo(() => ({ data, load }), [data, load]);
};

function joinMediaWithStats(allMedia: Media[], allAds: Ad[], allStatistics: Statistics[]): MediaWithStats[] {
    // Filter to LINE-only entities (this hook only queries LINE endpoints)
    const lineMedia = allMedia.filter(
        (m): m is Line.Media & { platform: AdPlatform.Line } => m.platform === AdPlatform.Line,
    );
    const lineAds = allAds.filter(
        (a): a is { platform: AdPlatform.Line } & Line.Ad & { externalAdsManagerUrl?: string } =>
            a.platform === AdPlatform.Line,
    );

    return lineMedia.map((mediaItem) => {
        const mediaAds = lineAds.filter((ad) => {
            const creative = ad.creative;
            return (
                creative?.image?.id === mediaItem.id ||
                creative?.video?.id === mediaItem.id ||
                creative?.smallImage?.id === mediaItem.id ||
                creative?.animation?.id === mediaItem.id
            );
        });

        const adIds = new Set(mediaAds.map((ad) => ad.id.toString()));
        const mediaStats = allStatistics.filter((stat) => adIds.has(stat.adId));

        return {
            id: mediaItem.id,
            type: mediaItem.mediaType,
            name: mediaItem.name,
            url: mediaItem.object?.sourceUrl,
            createdDate: mediaItem.createdDate,
            ads: mediaStats.map((stat) => ({
                id: stat.adId,
                name: stat.adName,
                cost: stat.cost,
                currency: stat.currency,
            })),
        };
    });
}
